import { verifyIsDuplicateData } from '@/helpers/verifyIsDuplicateData'
import { useQueryClient } from '@tanstack/vue-query'
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import { computed, reactive, ref } from 'vue'
import { useToast } from 'vue-toastification'

import useHasPermissionUser from '@/composables/useHasPermissionUser'

import { Brand } from '@/internal/modules/providers/interfaces/Brand'

import useChains from '../../chain/composables/useChains'
import useBrands from './useBrands'
import useSearchBrands from './useSearchBrands'

const useBrandForm = (brand?: Brand) => {
	const toast = useToast()
	const queryClient = useQueryClient()

	const modalAction = ref(false)

	const actionModal = () => {
		modalAction.value = !modalAction.value
	}

	const modalActionEdit = ref(false)

	const actionEdit = () => {
		modalActionEdit.value = !modalActionEdit.value
	}

	const { hasPermissionUser } = useHasPermissionUser()

	const isCoincidence = ref(false)
	const isConfirmed = ref(false)

	interface Form {
		id?: number
		name: string
		chain: { id: number; name: string } | undefined
		logo: File | string | null
	}

	const form = reactive<Form>({
		id: 0 || brand?.id,
		name: brand?.name ?? '',
		chain:
			brand?.chainId ? { id: brand.chainId, name: brand.chainName } : undefined,
		logo: null,
	})

	const isModifying = ref(false)

	const rulesForm = {
		name: {
			required: helpers.withMessage('El nombre es requerido', required),
		},
	}

	const formVuelidate = useVuelidate(rulesForm, form)

	const {
		data: brands,
		setQuery,
		refetch: refetchBrands,
	} = useSearchBrands({
		immediate: false,
		queryKey: 'list-brands-dupe',
	})

	const { createChainBrandAsync, createChainBrandLoading } = useChains({
		immediate: false,
	})

	const {
		updateMutationAsync,
		updateMutationLoading,
		createMediaMutationAsync,
		createMediaMutationLoading,
	} = useBrands({
		immediate: false,
	})

	const duplicateBrands = computed(() => {
		return brands.value?.data.filter((v) => v.name === form.name)
	})

	const handleEvaluation = async () => {
		await verifyIsDuplicateData({
			data: brands.value?.data!,
			form: form,
			setQuery,
			refetch: async () => {
				await refetchBrands()
			},
			formVuelidate,
			duplicateItems: duplicateBrands,
			toast,
			handleCreate,
			isConfirmed,
			isCoincidence,
		})
	}

	const handleCreate = async () => {
		isConfirmed.value = true

		const data = await createChainBrandAsync({
			name: form.name.toLowerCase(),
			chainId: form.chain?.id!,
		})

		if (form.logo) {
			await createMediaMutationAsync({
				id: data.id,
				media: form.logo! as File,
				collection: 'logo',
			})
		}

		if (data) {
			actionModal()
			clearState()

			await queryClient.invalidateQueries(['chain-brands'])
			await queryClient.invalidateQueries(['brands', 'list-brands'])
			await queryClient.invalidateQueries(['brands', 'list-brands-dupe'])
		}
	}

	const clearState = async () => {
		form.id = 0
		form.name = ''
		form.chain = undefined
		isCoincidence.value = false
		isConfirmed.value = false
		isModifying.value = false
		form.logo = null
	}

	const handleEvaluationEdit = async () => {
		for (const field of formVuelidate.value.$silentErrors) {
			if (!Array.isArray(field.$message)) {
				toast.error(field.$message)
				return
			}
		}

		if (hasPermissionUser('update-internal', 'App\\Models\\Brand')) {
			isConfirmed.value = true

			setQuery(form.name?.toLocaleLowerCase())
			await refetchBrands()

			if (brands.value?.data.length && form.name !== brand?.name) {
				isCoincidence.value = true
			} else {
				await handleEdit()
			}
		} else {
			await createMediaMutationAsync({
				id: form.id!,
				media: form.logo! as File,
				collection: 'logo',
			})

			isModifying.value = false

			toast.success(`Marca actualizada con éxito`)

			await queryClient.invalidateQueries(['brand'])
		}
	}

	const handleEdit = async () => {
		isConfirmed.value = true

		const data = await updateMutationAsync({
			id: form.id!,
			name: form.name,
			chainId: form.chain?.id!,
		})

		if (isModifying.value) {
			await createMediaMutationAsync({
				id: data.id,
				media: form.logo! as File,
				collection: 'logo',
			})
		}

		if (data) {
			actionEdit()

			isCoincidence.value = false
			isConfirmed.value = false
			isModifying.value = false
			await queryClient.invalidateQueries(['brand'])
			await queryClient.invalidateQueries(['brands', 'list-brands'])
			await queryClient.invalidateQueries(['brands', 'list-brands-dupe'])
		}
	}

	return {
		form,
		isCoincidence,
		isConfirmed,
		modalAction,
		handleEvaluation,
		handleCreate,
		clearState,
		actionModal,
		brands,
		createChainBrandLoading,

		handleEvaluationEdit,
		handleEdit,
		actionEdit,
		modalActionEdit,
		updateMutationLoading,

		isModifying,
		createMediaMutationLoading,

		createMediaMutationAsync,
	}
}

export default useBrandForm
